/**
 * Restore points (the API's "workbench backups"), shown on the Backups view beside the encrypted
 * backups and told apart from them: one per person, settings, projects and chat history, so a new
 * desktop starts where the person left off. Kept by the organisation: a person's own (when it was made, from which desktop,
 * how big; delete it), and for administrators everyone's. The content is never shown here: it is
 * restored by the person's own workbench after sign-in.
 *
 * Each is a list group (Layout.tsx): a row per restore point, its one menu at the end.
 */

import type { WorkbenchBackupSummary } from '@aioe/shared';
import { useState } from 'react';

import { routes } from '../api/client.ts';
import { ArrowRotateLeft } from '@gravity-ui/icons';

import { timeAgo, useLoad } from '../hooks.ts';
import { SubjectName } from './SubjectPicker.tsx';
import { ConfirmDialog, Empty, ErrorNote, ListGroup, ListRow } from './ui.tsx';
import { VIEW_TONES } from './ui/Art.tsx';
import { RowMenu } from './ui/RowMenu.tsx';
import { SearchBox } from './ui/SearchBox.tsx';
import { listCount, NoMatch, ShowMore, useListSearch, usePagedList } from '../pagedList.tsx';

const tone = VIEW_TONES.backups!;

const size = (bytes: number) => (bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`);
const line = (b: WorkbenchBackupSummary) => `${b.projects} ${b.projects === 1 ? 'project' : 'projects'}, ${b.repositories} ${b.repositories === 1 ? 'repository' : 'repositories'} · ${size(b.size)} · from ${b.device.name ?? 'a desktop'}${b.device.appVersion ? ` (AI Workbench ${b.device.appVersion})` : ''}`;

export function MyWorkbenchBackup() {
  const mine = useLoad(() => routes.myWorkbenchBackup().catch((e: { status?: number }) => (e.status === 404 ? null : Promise.reject(e))), []);
  const [error, setError] = useState<string>();
  const [confirming, setConfirming] = useState(false);
  const remove = async () => {
    setConfirming(false);
    try {
      await routes.deleteMyWorkbenchBackup();
      mine.reload();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  if (mine.data === undefined && !mine.error) return null;
  return (
    <>
      <ListGroup
        title="Your restore point"
        description="What sets up a new desktop when you sign in; separate from the encrypted backups."
        empty={
          mine.data === null ? (
            <Empty compact tone={tone} icon={<ArrowRotateLeft />} title="No restore point yet" body="While you are signed in, AI Workbench keeps your settings, projects and chat history here, so a new desktop starts where you left off." />
          ) : undefined
        }
      >
        {mine.data ? (
          <ListRow
            icon={<ArrowRotateLeft />}
            title={`Backed up ${timeAgo(mine.data.storedAt)}`}
            description={line(mine.data)}
            compactControl
            control={<RowMenu label="More for your restore point" items={[{ id: 'delete', label: 'Delete', danger: true }]} onAction={() => setConfirming(true)} />}
          />
        ) : null}
        {mine.error ? <ErrorNote error={mine.error} /> : null}
      </ListGroup>
      <ErrorNote error={error} />
      <ConfirmDialog isOpen={confirming} title="Delete your restore point" confirmLabel="Delete" danger onCancel={() => setConfirming(false)} onConfirm={remove}>
        A new desktop will start empty until your workbench keeps one again. Your encrypted backups are not touched.
      </ConfirmDialog>
    </>
  );
}

export function WorkbenchBackupsAdmin() {
  const search = useListSearch();
  const list = usePagedList((cursor) => routes.workbenchBackupsPage({ q: search.q, cursor }), { q: search.q }, { keyOf: (b) => b.userId });
  const [error, setError] = useState<string>();
  const [removing, setRemoving] = useState<string>();
  if (!list.loaded && !list.error) return null;
  // Not an administrator (or the route is refused): nothing to show.
  if (!list.loaded) return null;
  const remove = async (userId: string) => {
    setRemoving(undefined);
    try {
      await routes.deleteWorkbenchBackup(userId);
      list.reload();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  return (
    <>
      <ListGroup
        title="Everyone’s restore points"
        count={listCount(list.items.length, { one: 'person', many: 'people' }, { total: search.q ? undefined : list.extra?.total, hasMore: list.hasMore, searched: search.q !== '' })}
        description="One per person, sealed, credentials masked; only their own workbench restores it."
        actions={list.extra?.total === undefined || list.extra.total > 6 || search.q ? <SearchBox label="Search people" value={search.typed} onChange={search.setTyped} placeholder="Search people" className="w-40 max-w-full @lg:w-56" /> : undefined}
        empty={list.items.length === 0 && search.q ? <NoMatch tone={tone} icon={<ArrowRotateLeft />} q={search.q} noun="people" onClear={search.clear} /> : list.items.length === 0 ? <Empty compact tone={tone} icon={<ArrowRotateLeft />} title="Nobody has a restore point yet" body="Each person’s AI Workbench keeps one here while they are signed in." /> : undefined}
      >
        {list.items.map((b) => (
          <ListRow
            key={b.userId}
            icon={<ArrowRotateLeft />}
            title={<SubjectName id={b.userId} fallback={b.userId} />}
            description={`Backed up ${timeAgo(b.storedAt)} · ${line(b)}`}
            compactControl
            control={<RowMenu label="More for this restore point" items={[{ id: 'delete', label: 'Delete', danger: true }]} onAction={() => setRemoving(b.userId)} />}
          />
        ))}
        {list.hasMore ? <ShowMore onPress={list.showMore} pending={list.loadingMore} /> : null}
      </ListGroup>
      <ErrorNote error={error} />
      <ConfirmDialog isOpen={!!removing} title="Delete this person’s restore point" confirmLabel="Delete" danger onCancel={() => setRemoving(undefined)} onConfirm={() => removing && remove(removing)}>
        Their next new desktop starts empty. Their encrypted backups are not touched.
      </ConfirmDialog>
    </>
  );
}
